import {
  PositionSelectOptionsDocument,
  type PositionSelectOptionsQuery,
  type PositionSelectOptionsQueryVariables,
  PositionsForProjectDocument,
  type PositionsForProjectQuery,
  type PositionsForProjectQueryVariables,
} from "@src/__generated__/graphql";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import mapToOptions from "@src/utils/map-to-options";
import type {
  toWorkTypeOptionsParams,
  WorkTypeOption,
} from "@src/utils/map-to-options/workTypes";
import { action, computed, makeObservable, observable } from "mobx";

export class PositionSelectStore {
  @observable.ref
  options: WorkTypeOption[] = [];

  private positionOptionsFetcher = new FetchHelper<
    PositionSelectOptionsQuery,
    PositionSelectOptionsQueryVariables
  >(PositionSelectOptionsDocument);

  private projectPositionsFetcher = new FetchHelper<
    PositionsForProjectQuery,
    PositionsForProjectQueryVariables
  >(PositionsForProjectDocument);

  constructor() {
    makeObservable(this);
  }

  @computed
  get isLoading(): boolean {
    return (
      this.positionOptionsFetcher.isLoading.value ||
      this.projectPositionsFetcher.isLoading.value
    );
  }

  /** Fetched options plus the given ones that are missing from them. */
  optionsWith = (extraOptions: WorkTypeOption[] = []): WorkTypeOption[] => {
    const missing = extraOptions.filter(
      (extra) => !this.options.some(({ value }) => value === extra.value),
    );

    return [...missing, ...this.options];
  };

  @action.bound
  async fetchPositionOptions(
    optionModifier: toWorkTypeOptionsParams["modifier"] = (option) => option,
  ): Promise<void> {
    const [data, error] = await this.positionOptionsFetcher.fetch({});

    if (error || !data?.timeTrackingWorkTypes) {
      this.clearOptions();
      return;
    }

    this.options = mapToOptions.workTypes(
      data.timeTrackingWorkTypes,
      optionModifier,
    );
  }

  @action.bound
  async fetchProjectPositions(
    projectId: string,
    optionModifier: toWorkTypeOptionsParams["modifier"] = (option) => option,
  ): Promise<void> {
    const [data, error] = await this.projectPositionsFetcher.fetch({
      projectId,
    });

    if (error || !data?.projectForTask?.availableTimeTrackingWorkTypes) {
      this.clearOptions();
      return;
    }

    this.options = mapToOptions.workTypes(
      data.projectForTask.availableTimeTrackingWorkTypes,
      optionModifier,
    );
  }

  @action.bound
  clearOptions(): void {
    this.options = [];
  }
}
